import {
  Button,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  ActiveSearchContext,
  SearchContext,
  isChannelContext,
} from "../types";

const CONTEXT_LABELS: Record<SearchContext, string> = {
  all: "All",
  series: "Series",
  releases: "Releases",
  creators: "Creators",
  bundles: "Bundles",
};

// On browse: no "All" (tabs handle that). Off browse: include "All".
const BROWSE_CONTEXTS: SearchContext[] = ["series", "releases", "creators", "bundles"];
const OFF_BROWSE_CONTEXTS: SearchContext[] = ["all", "series", "releases", "creators", "bundles"];

interface SearchContextSelectorProps {
  context: ActiveSearchContext;
  channelIcon?: string;
  channelDisplayName?: string;
  isOnChannelPage?: boolean;
  isOnBrowsePage?: boolean;
  onSelect: (ctx: SearchContext) => void;
  onSelectChannel?: () => void;
}

export function SearchContextSelector({
  context,
  channelIcon,
  channelDisplayName,
  isOnChannelPage,
  isOnBrowsePage,
  onSelect,
  onSelectChannel,
}: SearchContextSelectorProps) {
  const isChannel = isChannelContext(context);
  const label = isChannel
    ? (channelDisplayName ?? context.browseKey)
    : CONTEXT_LABELS[context];
  const selectableContexts = isOnBrowsePage ? BROWSE_CONTEXTS : OFF_BROWSE_CONTEXTS;

  return (
    <Menu placement="bottom-start" matchWidth>
      <MenuButton
        as={Button}
        variant="searchContext"
        rightIcon={<ChevronDownIcon boxSize="24px" />}
        leftIcon={
          isChannel && channelIcon ? (
            <Image src={channelIcon} alt="" boxSize="24px" objectFit="contain" />
          ) : undefined
        }
        flexShrink={0}
        fontSize="md"
        iconSpacing="4px"
      >
        {label}
      </MenuButton>
      <MenuList
        bg="charcoal.charcoal"
        border="1px solid"
        borderColor="transparent.white.10"
        borderRadius="12px"
        minW="150px"
        py="4px"
        zIndex="popover"
        motionProps={{
          variants: {
            enter: {
              visibility: "visible",
              opacity: 1,
              y: 0,
              transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
            },
            exit: {
              transitionEnd: { visibility: "hidden" },
              opacity: 0,
              y: -8,
              transition: { duration: 0.1, ease: "easeOut" },
            },
          },
        }}
      >
        {selectableContexts.map((ctx) => (
          <MenuItem
            key={ctx}
            bg={!isChannel && context === ctx ? "transparent.white.10" : "transparent"}
            color="dune.100"
            fontWeight={!isChannel && context === ctx ? "bold" : "normal"}
            fontSize="md"
            _hover={{ bg: "transparent.white.20" }}
            onClick={() => onSelect(ctx)}
          >
            {CONTEXT_LABELS[ctx]}
          </MenuItem>
        ))}
        {isOnChannelPage && channelDisplayName && (
          <MenuItem
            bg={isChannel ? "transparent.white.10" : "transparent"}
            color="dune.100"
            fontWeight={isChannel ? "bold" : "normal"}
            fontSize="md"
            _hover={{ bg: "transparent.white.20" }}
            onClick={() => onSelectChannel?.()}
            icon={
              channelIcon ? (
                <Image src={channelIcon} alt="" boxSize="20px" objectFit="contain" />
              ) : undefined
            }
          >
            {channelDisplayName}
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );
}
